import React from "react";
import { Bell, CreditCard, PackageCheck, Printer, ClipboardList, MessageCircle } from "lucide-react";

const normalize = (value) => String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
const normalizeStatus = (value) => {
  const status = normalize(value || "new");
  if (status === "canceled") return "cancelled";
  if (["ready_for_dispatch", "ready_to_dispatch"].includes(status)) return "ready";
  if (status === "dispatched") return "shipped";
  return status;
};
const isManualOrder = (order) => {
  const source = normalize(order?.source || order?.order_source);
  return ["social_media", "phone", "walk_in", "whatsapp", "manual", "manual_order", "instagram", "facebook"].includes(source)
    || order?.product_slug === "manual-custom-order"
    || (Array.isArray(order?.items) ? order.items : []).some((item) => item?.product_slug === "manual-custom-order");
};
const isCodOrder = (order) => ["cod", "cash_on_delivery"].includes(normalize(order?.payment_method));

export default function AdminDashboardActionCenter({ orders = [], quotes = [], unread = 0, onNavigate }) {
  // Match the dashboard's existing rule: hide unpaid online checkout attempts,
  // but retain manual orders and COD orders until payment/collection is recorded.
  const eligibleOrders = orders.filter((order) => {
    if (normalizeStatus(order.status) === "cancelled") return false;
    return isManualOrder(order) || isCodOrder(order) || normalize(order.payment_status) === "paid";
  });
  const countStatus = (...statuses) => eligibleOrders.filter((order) => statuses.includes(normalizeStatus(order.status))).length;
  const unpaid = eligibleOrders.filter((order) => normalize(order.payment_status || "pending") !== "paid").length;
  const unreadCount = Number(unread);
  const safeUnreadCount = Number.isFinite(unreadCount) && unreadCount > 0 ? Math.floor(unreadCount) : 0;

  const actions = [
    { title: "New orders", detail: "Review and confirm", count: countStatus("new"), icon: ClipboardList, tab: "Orders", tone: "rust" },
    { title: "In production", detail: "Confirmed, processing or printed", count: countStatus("confirmed", "processing", "printed"), icon: Printer, tab: "Orders", tone: "neutral" },
    { title: "Ready to dispatch", detail: "Arrange courier handover", count: countStatus("ready"), icon: PackageCheck, tab: "Orders", tone: "green" },
    { title: "Payment to review", detail: "Check unpaid order records", count: unpaid, icon: CreditCard, tab: "Orders", tone: "neutral" },
    { title: "Custom enquiries", detail: "Follow up with customers", count: quotes.length, icon: MessageCircle, tab: "Custom Orders", tone: "neutral" },
    { title: "Unread notifications", detail: "Review recent alerts", count: safeUnreadCount, icon: Bell, tab: "Notifications", tone: "neutral" },
  ];

  return (
    <section className="card-pmt p-4 sm:p-5" aria-labelledby="admin-action-center-title" data-testid="admin-action-center">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline">Work queue</div>
          <h2 id="admin-action-center-title" className="mt-1 text-xl font-bold">Needs your attention</h2>
          <p className="mt-1 text-sm text-stone-600">Select a task to jump into the relevant admin section.</p>
        </div>
        <span className="text-xs text-stone-500">Counts use the currently loaded records</span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {actions.map(({ title, detail, count, icon: Icon, tab, tone }) => (
          <button
            key={title}
            type="button"
            onClick={() => onNavigate(tab)}
            className="flex min-w-0 items-center gap-3 rounded-lg border border-[#E6DFD5] bg-white p-3 text-left transition-colors hover:border-[#D95328] hover:bg-[#FAF7F2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D95328]"
            data-testid={`dashboard-action-${tab.toLowerCase().replace(/\s+/g, "-")}-${title.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${tone === "rust" ? "bg-[#FCE8DF] text-[#D95328]" : tone === "green" ? "bg-emerald-50 text-emerald-700" : "bg-[#F3EDE2] text-[#141413]"}`}>
              <Icon size={18} aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">{title}</span>
              <span className="mt-1 block text-xs leading-relaxed text-stone-500">{detail}</span>
            </span>
            <span className="min-w-8 text-right text-2xl font-black" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{count}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
